import {
  Component,
  inject,
  OnInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  NgZone,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { PopoverModule } from 'primeng/popover';
import { BadgeModule } from 'primeng/badge';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { TooltipModule } from 'primeng/tooltip';
import { SkeletonModule } from 'primeng/skeleton';
import { NotificationService } from '../../services/notification.service';
import { SimpleInAppNotification } from '@ics-referral-hub/shared-types';
import { Popover } from 'primeng/popover';

@Component({
  selector: 'app-notification-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    ButtonModule,
    PopoverModule,
    BadgeModule,
    ScrollPanelModule,
    TooltipModule,
    SkeletonModule,
  ],
  template: `
    <div class="notification-wrapper">
      <!-- Bell Icon with Badge -->
      <p-button
        [style]="{
          background: 'transparent',
          border: 'none',
          color: '#0b435b',
          padding: '8px',
          'border-radius': '6px',
        }"
        (click)="toggleNotifications($event)"
        [pTooltip]="
          newCount > 0 ? newCount + ' new notifications' : 'Notifications'
        "
        tooltipPosition="bottom"
      >
        <div class="bell-container" [style]="{ position: 'relative' }">
          <i
            class="pi pi-bell notification-icon"
            [class.has-new]="newCount > 0"
            [style]="{
              'font-size': '20px',
            }"
          ></i>
          <span
            *ngIf="newCount > 0"
            class="notification-badge"
            [attr.aria-label]="newCount + ' new notifications'"
          >
            {{ newCount > 99 ? '99+' : newCount }}
          </span>
        </div>
      </p-button>

      <!-- Notification Dropdown Panel -->
      <p-popover
        #notificationPanel
        [style]="{
          width: '400px',
          'max-height': '500px',
        }"
        appendTo="body"
      >
        <div class="notification-dropdown">
          <!-- Header -->
          <div class="notification-header">
            <div class="header-content">
              <h3 class="header-title">Notifications</h3>
              <button
                class="mark-read-btn"
                (click)="markAllAsRead()"
                [disabled]="
                  newCount === 0 ||
                  notificationService.markAsReadMutation.isPending()
                "
                *ngIf="newCount > 0"
              >
                <i class="pi pi-check"></i>
                Mark all read
              </button>
            </div>
          </div>

          <!-- Notifications List -->
          <div class="notification-content">
            <!-- Loading State -->
            <div *ngIf="isLoading" class="loading-state">
              <p-skeleton
                height="60px"
                class="mb-2"
                *ngFor="let item of [1, 2, 3]"
              ></p-skeleton>
            </div>

            <!-- Empty State -->
            <div
              *ngIf="!isLoading && notifications.length === 0"
              class="empty-state"
            >
              <div class="empty-icon">
                <i class="pi pi-bell-slash"></i>
              </div>
              <p class="empty-message">No notifications yet</p>
            </div>

            <!-- Notifications -->
            <div
              *ngIf="!isLoading && notifications.length > 0"
              class="notifications-list"
            >
              <div
                *ngFor="
                  let notification of notifications;
                  trackBy: trackByNotificationId
                "
                class="notification-item"
                [class.is-new]="isNewNotification(notification)"
                (click)="onNotificationClick(notification)"
              >
                <div class="notification-content-wrapper">
                  <div class="notification-main">
                    <div class="notification-title">
                      {{ notification.title }}
                    </div>
                    <div class="notification-message">
                      {{ notification.message }}
                    </div>
                  </div>
                  <div class="notification-time">
                    {{ getFormattedTimestamp(notification) }}
                  </div>
                </div>
                <div
                  class="notification-indicator"
                  *ngIf="isNewNotification(notification)"
                >
                  <div class="new-dot"></div>
                </div>
              </div>
            </div>
          </div>

          <!-- Footer -->
          <div class="notification-footer" *ngIf="notifications.length > 0">
            <div class="footer-info">
              {{ notifications.length }} notifications
              <span *ngIf="newCount > 0"> • {{ newCount }} new</span>
            </div>
          </div>
        </div>
      </p-popover>
    </div>
  `,
  styles: [
    `
      .notification-wrapper {
        position: relative;
      }

      .bell-container {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .notification-badge {
        position: absolute;
        top: -10px;
        right: -4px;
        background-color: #dc3545;
        color: white;
        font-size: 11px;
        font-weight: 600;
        min-width: 18px;
        height: 18px;
        line-height: 18px;
        text-align: center;
        border-radius: 9px;
        padding: 0 4px;
        box-sizing: border-box;
        border: 2px solid white;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
        z-index: 10;
      }

      .notification-icon.has-new {
        animation: subtle-pulse 2s infinite;
      }

      @keyframes subtle-pulse {
        0%,
        100% {
          transform: scale(1);
        }
        50% {
          transform: scale(1.05);
        }
      }

      .notification-dropdown {
        display: flex;
        flex-direction: column;
        max-height: 500px;
      }

      .notification-header {
        padding: 16px 20px 12px;
        border-bottom: 1px solid #e5e7eb;
        background: #f8fcfd;
      }

      .header-content {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }

      .header-title {
        font-family: 'Open Sans', sans-serif;
        font-size: 18px;
        font-weight: 600;
        color: #0b435b;
        margin: 0;
      }

      .mark-read-btn {
        display: flex;
        align-items: center;
        gap: 6px;
        background: #0b435b;
        color: white;
        border: none;
        padding: 6px 12px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        transition: background-color 0.2s;
      }

      .mark-read-btn:hover:not(:disabled) {
        background: #0b738e;
      }

      .mark-read-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      .notification-content {
        flex: 1;
        overflow: hidden;
      }

      .loading-state {
        padding: 16px 20px;
      }

      .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 40px 20px;
        text-align: center;
      }

      .empty-icon {
        font-size: 48px;
        color: #9ca3af;
        margin-bottom: 12px;
      }

      .empty-message {
        font-family: 'Open Sans', sans-serif;
        font-size: 14px;
        color: #6b7280;
        margin: 0;
      }

      .notifications-list {
        max-height: 360px;
        overflow-y: auto;
      }

      .notification-item {
        display: flex;
        padding: 16px 20px;
        border-bottom: 1px solid #f1f5f9;
        cursor: pointer;
        transition: background-color 0.2s;
        position: relative;
      }

      .notification-item:hover {
        background: #f8fcfd;
      }

      .notification-item.is-new {
        background: #f0f9ff;
        border-left: 3px solid #0b738e;
      }

      .notification-content-wrapper {
        flex: 1;
        display: flex;
        flex-direction: column;
      }

      .notification-main {
        flex: 1;
      }

      .notification-title {
        font-family: 'Open Sans', sans-serif;
        font-size: 14px;
        font-weight: 600;
        color: #0b435b;
        margin-bottom: 4px;
        line-height: 1.4;
      }

      .notification-message {
        font-family: 'Open Sans', sans-serif;
        font-size: 13px;
        color: #51534a;
        line-height: 1.4;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .notification-time {
        font-family: 'Open Sans', sans-serif;
        font-size: 12px;
        color: #9ca3af;
        margin-top: 8px;
      }

      .notification-indicator {
        display: flex;
        align-items: flex-start;
        padding-top: 4px;
        margin-left: 12px;
      }

      .new-dot {
        width: 8px;
        height: 8px;
        background: #0b738e;
        border-radius: 50%;
      }

      .notification-footer {
        padding: 12px 20px;
        border-top: 1px solid #e5e7eb;
        background: #f8fcfd;
      }

      .footer-info {
        font-family: 'Open Sans', sans-serif;
        font-size: 12px;
        color: #6b7280;
        text-align: center;
      }

      /* Custom scrollbar for notifications list */
      .notifications-list::-webkit-scrollbar {
        width: 6px;
      }

      .notifications-list::-webkit-scrollbar-track {
        background: #f1f5f9;
      }

      .notifications-list::-webkit-scrollbar-thumb {
        background: #cbd5e1;
        border-radius: 3px;
      }

      .notifications-list::-webkit-scrollbar-thumb:hover {
        background: #94a3b8;
      }
    `,
  ],
})
export class NotificationDropdownComponent implements OnInit, OnDestroy {
  @ViewChild('notificationPanel') notificationPanel!: Popover;

  public notificationService = inject(NotificationService);
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private timestampCache = new Map<string, string>();
  private updateInterval: any;

  constructor() {
    // Update timestamp cache whenever notifications change
    effect(() => {
      // Access notifications to trigger effect
      const notifications = this.notificationService.notifications();
      // Update cache outside Angular's change detection
      this.ngZone.runOutsideAngular(() => {
        this.updateTimestampCache();
      });
    });
  }

  ngOnInit(): void {
    // Component will automatically get data from TanStack Query
    // Initialize timestamp cache
    this.updateTimestampCache();

    // Update timestamps every 30 seconds outside Angular's change detection
    this.ngZone.runOutsideAngular(() => {
      this.updateInterval = setInterval(() => {
        this.updateTimestampCache();
        // Manually trigger change detection
        this.ngZone.run(() => {
          this.cdr.markForCheck();
        });
      }, 30000); // Update every 30 seconds
    });
  }

  ngOnDestroy(): void {
    // Clean up interval
    if (this.updateInterval) {
      clearInterval(this.updateInterval);
    }
  }

  private updateTimestampCache(): void {
    const now = Date.now();
    this.notifications.forEach((notification) => {
      const formattedTime = this.calculateTimeAgo(now, notification.createdAt);
      this.timestampCache.set(notification._id || '', formattedTime);
    });
  }

  get notifications(): SimpleInAppNotification[] {
    return this.notificationService.notifications();
  }

  get newCount(): number {
    return this.notificationService.newCount();
  }

  get isLoading(): boolean {
    return this.notificationService.isLoading;
  }

  toggleNotifications(event: Event): void {
    // Toggle the popover panel
    this.notificationPanel.toggle(event);

    // Mark all notifications (in-app + messages) as read after 3 seconds
    setTimeout(() => {
      if (this.notificationService.newCount() > 0) {
        this.markAllAsRead();
      }
    }, 3000);
  }

  markAllAsRead(): void {
    this.notificationService.markAsRead();
  }

  onNotificationClick(notification: SimpleInAppNotification): void {
    // Close the popover first so it doesn't block navigation across modules
    this.notificationPanel.hide();
    this.notificationService.handleNotificationClick(notification);
  }

  isNewNotification(notification: SimpleInAppNotification): boolean {
    // Message threads use unreadCount → isRead; stay in the list after mark-read
    // but drop the new highlight / badge contribution.
    if (notification._id?.startsWith('message-')) {
      return !notification.isRead;
    }
    return notification.createdAt > this.notificationService.lastReadAt;
  }

  trackByNotificationId(
    index: number,
    notification: SimpleInAppNotification,
  ): string {
    return notification._id || index.toString();
  }

  getFormattedTimestamp(notification: SimpleInAppNotification): string {
    const cached = this.timestampCache.get(notification._id || '');
    if (cached) {
      return cached;
    }
    // Fallback if cache miss
    const now = Date.now();
    return this.calculateTimeAgo(now, notification.createdAt);
  }

  private calculateTimeAgo(now: number, timestamp: number): string {
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    // For older notifications, return date
    return new Date(timestamp).toLocaleDateString();
  }
}
